import { useAccountStoreShallow } from "@/features/account-management/store/account.store";
import {
  useConfirmMfaEmailCodeMutation,
  useSendMfaEmailCodeMutation,
} from "@/shared/services/mfa/mfa.mutation";
import Button from "@components/ui/button";
import Dialog from "@components/ui/dialog";
import { OtpDigits } from "@components/ui/otp-digits";
import { auth } from "@lib/firebase";
import { MfaSetupDialog } from "@pages/settings/modules/security-privacy/components/mfa-setup-dialog";
import { IconAlertCircle, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { getUserSignUpType } from "../utils/auth.utils";
import { isTotpEnrolled, refreshEmailVerified } from "../utils/mfa.utils";

const GENERIC_ERROR = "Something went wrong. Please try again.";

const apiErrorCode = (error: unknown): string | undefined =>
  (error as { response?: { data?: { code?: string } } }).response?.data?.code;

const EMAIL_CODE_ERRORS: Record<string, string> = {
  RESEND_COOLDOWN: "Please wait a minute before requesting another code.",
  INVALID_CODE: "That code didn't work. Check your email and try again.",
  CODE_EXPIRED: "That code expired. Select Resend email to get a new one.",
  TOO_MANY_ATTEMPTS:
    "Too many attempts. Select Resend email to get a new code.",
};

type PromptStep = "verify-email" | "verify-code" | "setup" | null;

export const MfaEnforcementDialog = () => {
  const account = useAccountStoreShallow((state) => state.account);
  const [step, setStep] = useState<PromptStep>(null);
  const [emailCode, setEmailCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const promptedAccountIdRef = useRef<string | null>(null);
  const sendEmailCode = useSendMfaEmailCodeMutation();
  const confirmEmailCode = useConfirmMfaEmailCodeMutation();

  useEffect(() => {
    if (!account?._id || !account.mfaRequired) return;
    if (promptedAccountIdRef.current === account._id) return;

    const provider = getUserSignUpType();
    if (provider?.startsWith("saml.") || isTotpEnrolled()) return;

    promptedAccountIdRef.current = account._id;
    setStep(
      provider === "password" && !auth.currentUser?.emailVerified
        ? "verify-email"
        : "setup",
    );
  }, [account?._id, account?.mfaRequired]);

  const handleClose = () => {
    setError(null);
    setEmailCode("");
    setStep(null);
  };

  const handleSendEmailCode = async () => {
    try {
      setError(null);
      await sendEmailCode.mutateAsync();
      setEmailCode("");
      setStep("verify-code");
    } catch (sendError) {
      const code = apiErrorCode(sendError);
      setError((code && EMAIL_CODE_ERRORS[code]) || GENERIC_ERROR);
    }
  };

  const handleVerifyEmailCode = async () => {
    try {
      setError(null);
      await confirmEmailCode.mutateAsync(emailCode);
      await refreshEmailVerified();
      setEmailCode("");
      setStep("setup");
    } catch (verifyError) {
      const code = apiErrorCode(verifyError);
      setError((code && EMAIL_CODE_ERRORS[code]) || GENERIC_ERROR);
      setEmailCode("");
    }
  };

  return (
    <>
      <Dialog
        open={step === "verify-email" || step === "verify-code"}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        closeOnOutsideClick={handleClose}
      >
        <Dialog.Content className="tw-w-[560px] tw-max-w-[calc(100vw-32px)]">
          <div className="tw-relative tw-flex tw-w-full tw-flex-col tw-gap-8 tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-sm tw-outline tw-outline-1 tw-outline-blue/5">
            <button
              type="button"
              aria-label="Close two-factor authentication setup"
              onClick={handleClose}
              className="tw-bg-transparent tw-absolute tw-right-4 tw-top-4 tw-flex tw-size-8 tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-text-blue-700 hover:tw-bg-blue-50"
            >
              <IconX className="tw-size-5" />
            </button>
            <div className="tw-flex tw-flex-col tw-gap-2 tw-pr-8">
              <p className="tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                Verify your email
              </p>
              <p className="tw-text-sm tw-leading-5 tw-text-blue-700">
                Verify your email address before setting up two-factor
                authentication.
              </p>
            </div>

            {step === "verify-email" ? (
              <div>
                <Button
                  size="default"
                  onClick={handleSendEmailCode}
                  isLoading={sendEmailCode.isPending}
                >
                  Send verification email
                </Button>
              </div>
            ) : (
              <div className="tw-flex tw-flex-col tw-items-start tw-gap-4">
                <OtpDigits
                  value={emailCode}
                  onChange={setEmailCode}
                  disabled={confirmEmailCode.isPending}
                />
                <div className="tw-flex tw-gap-2">
                  <Button
                    size="default"
                    onClick={handleVerifyEmailCode}
                    isLoading={confirmEmailCode.isPending}
                    disabled={emailCode.length !== 6}
                  >
                    Verify
                  </Button>
                  <Button
                    variant="secondary"
                    size="default"
                    onClick={handleSendEmailCode}
                    isLoading={sendEmailCode.isPending}
                  >
                    Resend email
                  </Button>
                </div>
              </div>
            )}

            {error && (
              <div className="tw-flex tw-items-center tw-text-xs tw-text-red-500">
                <IconAlertCircle className="tw-mr-1.5 tw-size-4 tw-shrink-0" />
                {error}
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog>

      <MfaSetupDialog
        open={step === "setup"}
        intent="setup"
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        onEnrollmentChanged={() => undefined}
        onNeedsEmailVerification={() => setStep("verify-email")}
      />
    </>
  );
};
